import { useState, useEffect, useRef } from 'react';
import { Send, Smile, Paperclip, Image, Search, MoreVertical, ChevronLeft, Plus, X } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { User, Workspace } from '../../types';
import { chatService, ChatMessage, ChatRoom } from '../../services/chatService';

interface NewChatModalProps {
  onClose: () => void;
  onCreateChat: (type: 'direct' | 'group', name: string, members: string[]) => void;
  workspaceUsers: User[];
}

function NewChatModal({ onClose, onCreateChat, workspaceUsers }: NewChatModalProps) {
  const [chatType, setChatType] = useState<'direct' | 'group'>('direct');
  const [groupName, setGroupName] = useState('');
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (chatType === 'direct' && selectedUsers.length === 1) {
      const user = workspaceUsers.find(u => u.id === selectedUsers[0]);
      onCreateChat('direct', user?.firstName + ' ' + user?.lastName, selectedUsers);
    } else if (chatType === 'group' && groupName && selectedUsers.length > 0) {
      onCreateChat('group', groupName, selectedUsers);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-96">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold">New Chat</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">Chat Type</label>
            <div className="flex space-x-4">
              <label className="flex items-center">
                <input
                  type="radio"
                  value="direct"
                  checked={chatType === 'direct'}
                  onChange={(e) => setChatType(e.target.value as 'direct' | 'group')}
                  className="mr-2"
                />
                Direct Message
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  value="group"
                  checked={chatType === 'group'}
                  onChange={(e) => setChatType(e.target.value as 'direct' | 'group')}
                  className="mr-2"
                />
                Group Chat
              </label>
            </div>
          </div>

          {chatType === 'group' && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Group Name</label>
              <input
                type="text"
                value={groupName}
                onChange={(e) => setGroupName(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter group name"
                required
              />
            </div>
          )}

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select {chatType === 'direct' ? 'User' : 'Members'}
            </label>
            <div className="max-h-48 overflow-y-auto border border-gray-300 rounded-lg">
              {workspaceUsers.map((user) => (
                <label
                  key={user.id}
                  className="flex items-center p-2 hover:bg-gray-50 cursor-pointer"
                >
                  <input
                    type={chatType === 'direct' ? 'radio' : 'checkbox'}
                    value={user.id}
                    checked={selectedUsers.includes(user.id)}
                    onChange={(e) => {
                      if (chatType === 'direct') {
                        setSelectedUsers([e.target.value]);
                      } else {
                        setSelectedUsers(prev =>
                          e.target.checked
                            ? [...prev, e.target.value]
                            : prev.filter(id => id !== e.target.value)
                        );
                      }
                    }}
                    className="mr-2"
                  />
                  <span>{user.firstName} {user.lastName}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="flex justify-end space-x-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              disabled={
                (chatType === 'direct' && selectedUsers.length !== 1) ||
                (chatType === 'group' && (!groupName || selectedUsers.length === 0))
              }
            >
              Create Chat
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export function TeamChat() {
  const { currentWorkspace, user } = useAuth();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [workspaceUsers, setWorkspaceUsers] = useState<User[]>([]);
  const [onlineUsers, setOnlineUsers] = useState<string[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [selectedChat, setSelectedChat] = useState<ChatRoom | null>(null);
  const [chatRooms, setChatRooms] = useState<ChatRoom[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isMobileView, setIsMobileView] = useState(false);
  const [showChatList, setShowChatList] = useState(true);
  const [showNewChatModal, setShowNewChatModal] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const handleResize = () => {
      setIsMobileView(window.innerWidth < 768);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (!currentWorkspace || !user) return;

    const loadWorkspaceData = async () => {
      try {
        // Load workspace members instead of users
        const members = await chatService.getWorkspaceMembers(currentWorkspace.id);
        setWorkspaceUsers(members);

        // Load chat rooms
        const rooms = await chatService.getChatRooms(currentWorkspace.id);
        setChatRooms(rooms);

        // Create or get AI chat
        const aiChatId = await chatService.createAIChat(currentWorkspace.id, user.id);
        const aiChat = rooms.find(room => room.id === aiChatId);
        if (!aiChat) {
          // Refresh chat rooms to include the new AI chat
          const updatedRooms = await chatService.getChatRooms(currentWorkspace.id);
          setChatRooms(updatedRooms);
        }

        // Update online status
        await chatService.updateOnlineStatus(currentWorkspace.id, user.id, true);

        // Load online users
        const onlineUserIds = await chatService.getOnlineUsers(currentWorkspace.id);
        setOnlineUsers(onlineUserIds);
      } catch (error) {
        console.error('Error loading workspace data:', error);
      }
    };

    loadWorkspaceData();

    // Set up online status interval
    const onlineInterval = setInterval(async () => {
      if (user && currentWorkspace) {
        await chatService.updateOnlineStatus(currentWorkspace.id, user.id, true);
        const onlineUserIds = await chatService.getOnlineUsers(currentWorkspace.id);
        setOnlineUsers(onlineUserIds);
      }
    }, 30000); // Update every 30 seconds

    // Set up online status subscription for all workspace users
    const onlineSubscriptions = workspaceUsers.map(workspaceUser => 
      chatService.subscribeToOnlineStatus(currentWorkspace.id, workspaceUser.id, (status) => {
        setOnlineUsers(prev => 
          status.online 
            ? [...new Set([...prev, workspaceUser.id])]
            : prev.filter(id => id !== workspaceUser.id)
        );
      })
    );

    // Cleanup
    return () => {
      clearInterval(onlineInterval);
      onlineSubscriptions.forEach(unsubscribe => unsubscribe());
      if (user && currentWorkspace) {
        chatService.updateOnlineStatus(currentWorkspace.id, user.id, false).catch(console.error);
      }
    };
  }, [currentWorkspace, user]);

  useEffect(() => {
    if (!currentWorkspace || !selectedChat) return;

    const loadMessages = async () => {
      try {
        const messages = await chatService.getMessages(currentWorkspace.id, selectedChat.id);
        setMessages(messages);
        setIsLoading(false);
      } catch (error) {
        console.error('Error loading messages:', error);
        setIsLoading(false);
      }
    };

    const subscription = chatService.subscribeToMessages(currentWorkspace.id, selectedChat.id, (message) => {
      setMessages((prev) => [...prev, message]);
    });

    loadMessages();
    return () => {
      subscription();
    };
  }, [currentWorkspace, selectedChat]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !user || !currentWorkspace || !selectedChat) return;

    try {
      if (selectedChat.type === 'ai') {
        await chatService.sendMessageToAI(currentWorkspace.id, selectedChat.id, {
          content: newMessage,
          userId: user.id,
          userName: `${user.firstName} ${user.lastName}`,
          workspaceId: currentWorkspace.id,
          chatRoomId: selectedChat.id,
          ...(user.avatar && { userAvatar: user.avatar })
        });
      } else {
      await chatService.sendMessage(currentWorkspace.id, {
          content: newMessage,
        userId: user.id,
        userName: `${user.firstName} ${user.lastName}`,
        workspaceId: currentWorkspace.id,
          chatRoomId: selectedChat.id,
          ...(user.avatar && { userAvatar: user.avatar })
      });
      }
      setNewMessage('');
    } catch (error) {
      console.error('Error sending message:', error);
      alert('Failed to send message. Please try again.');
    }
  };

  const handleFileUpload = async (file: File) => {
    if (!currentWorkspace || !user || !selectedChat) return;

    setIsUploading(true);
    try {
      const attachment = await chatService.uploadAttachment(file, currentWorkspace.id);
      await chatService.sendMessage(currentWorkspace.id, {
        content: `Shared ${attachment.type === 'image' ? 'an image' : 'a file'}: ${attachment.name}`,
        userId: user.id,
        userName: `${user.firstName} ${user.lastName}`,
        workspaceId: currentWorkspace.id,
        chatRoomId: selectedChat.id,
        ...(user.avatar && { userAvatar: user.avatar }),
        attachments: [attachment]
      });
    } catch (error) {
      console.error('Error uploading file:', error);
      alert('Failed to upload file. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  const handleChatSelect = (chat: ChatRoom) => {
    setSelectedChat(chat);
    if (isMobileView) {
      setShowChatList(false);
    }
  };

  const filteredChats = chatRooms.filter(chat =>
    chat.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleCreateChat = async (type: 'direct' | 'group', name: string, members: string[]) => {
    if (!currentWorkspace || !user) return;

    try {
      const allMembers = [...members, user.id];
      const chatId = type === 'direct'
        ? await chatService.createDirectChat(currentWorkspace.id, allMembers, name)
        : await chatService.createGroupChat(currentWorkspace.id, allMembers, name);

      // Refresh chat rooms
      const rooms = await chatService.getChatRooms(currentWorkspace.id);
      setChatRooms(rooms);

      // Select the new chat
      const newChat = rooms.find(room => room.id === chatId);
      if (newChat) {
        setSelectedChat(newChat);
        if (isMobileView) {
          setShowChatList(false);
        }
      }
    } catch (error) {
      console.error('Error creating chat:', error);
      alert('Failed to create chat. Please try again.');
    }

    setShowNewChatModal(false);
  };

  const isUserOnline = (userId: string) => onlineUsers.includes(userId);

  const getUserName = (userId: string) => {
    const user = workspaceUsers.find(u => u.id === userId);
    return user ? `${user.firstName} ${user.lastName}` : 'Unknown User';
  };

  const getUserAvatar = (userId: string) => {
    const user = workspaceUsers.find(u => u.id === userId);
    return user?.avatar;
  };

  if (!currentWorkspace) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-gray-500">Please select a workspace to start chatting.</p>
      </div>
    );
  }

  return (
    <div className="flex h-[calc(100vh-4rem)] bg-white">
      {/* Chat List */}
      {(!isMobileView || showChatList) && (
        <div className="w-full md:w-80 border-r border-gray-200 flex flex-col">
          {/* Search Header */}
      <div className="p-4 border-b border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold">Chats</h2>
              <button
                onClick={() => setShowNewChatModal(true)}
                className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full"
                title="New Chat"
              >
                <Plus className="h-5 w-5" />
              </button>
            </div>
            <div className="relative">
              <Search className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search chats..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Chat List */}
          <div className="flex-1 overflow-y-auto">
            {filteredChats.map((chat) => {
              const otherUserId = chat.type === 'direct' 
                ? chat.members.find(id => id !== user?.id)
                : null;

              return (
                <button
                  key={chat.id}
                  onClick={() => handleChatSelect(chat)}
                  className={`w-full p-4 flex items-center space-x-3 hover:bg-gray-50 ${
                    selectedChat?.id === chat.id ? 'bg-blue-50' : ''
                  }`}
                >
                  <div className="flex-shrink-0 relative">
                    {chat.type === 'ai' ? (
                      <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center">
                        <img
                          src="/ai-avatar.svg"
                          alt="AI Assistant"
                          className="w-8 h-8"
                        />
                      </div>
                    ) : chat.type === 'direct' ? (
                      <>
                        {otherUserId && getUserAvatar(otherUserId) ? (
                          <img
                            src={getUserAvatar(otherUserId)}
                            alt={getUserName(otherUserId)}
                            className="w-12 h-12 rounded-full"
                    />
                  ) : (
                          <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                            <span className="text-blue-600 font-medium">
                              {otherUserId ? getUserName(otherUserId)[0] : '?'}
                            </span>
                          </div>
                        )}
                        {otherUserId && isUserOnline(otherUserId) && (
                          <span className="absolute bottom-0 right-0 block h-3 w-3 rounded-full bg-green-400 ring-2 ring-white" />
                        )}
                      </>
                    ) : (
                      <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                        <span className="text-green-600 font-medium">
                          {chat.name[0]}
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between">
                      <h3 className="text-sm font-medium text-gray-900 truncate">
                        {chat.type === 'ai' ? 'eGemini AI' :
                         chat.type === 'direct' && otherUserId
                          ? getUserName(otherUserId)
                          : chat.name}
                      </h3>
                      {chat.lastMessage && (
                        <span className="text-xs text-gray-500">
                          {new Date(chat.lastMessage.timestamp).toLocaleTimeString([], {
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                      </span>
                      )}
                    </div>
                    {chat.type === 'group' && (
                      <p className="text-xs text-gray-500 mb-1">
                        {chat.members.length} members
                        {' • '}
                        {chat.members.filter(isUserOnline).length} online
                      </p>
                    )}
                    {chat.lastMessage && (
                      <p className="text-sm text-gray-500 truncate">
                        {chat.type === 'ai' ? 'AI: ' : ''}{chat.lastMessage.content}
                      </p>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Chat Area */}
      <div className="flex-1 flex flex-col">
        {selectedChat ? (
          <>
            {/* Chat Header */}
            <div className="p-4 border-b border-gray-200 flex items-center">
              {isMobileView && (
                <button
                  onClick={() => setShowChatList(true)}
                  className="mr-2 p-1 hover:bg-gray-100 rounded-full"
                >
                  <ChevronLeft className="h-6 w-6" />
                </button>
              )}
              <div className="flex-1">
                <div className="flex items-center">
                  <h2 className="text-lg font-semibold">
                    {selectedChat.type === 'direct'
                      ? getUserName(selectedChat.members.find(id => id !== user?.id) || '')
                      : selectedChat.name}
                  </h2>
                  {selectedChat.type === 'direct' && (
                    <span className={`ml-2 h-2 w-2 rounded-full ${
                      isUserOnline(selectedChat.members.find(id => id !== user?.id) || '')
                        ? 'bg-green-400'
                        : 'bg-gray-300'
                    }`} />
                  )}
                </div>
                <p className="text-sm text-gray-500">
                  {selectedChat.type === 'group'
                    ? `${selectedChat.members.length} members • ${
                        selectedChat.members.filter(isUserOnline).length
                      } online`
                    : isUserOnline(selectedChat.members.find(id => id !== user?.id) || '')
                    ? 'Online'
                    : 'Offline'}
                </p>
              </div>
              <button className="p-2 hover:bg-gray-100 rounded-full">
                <MoreVertical className="h-5 w-5 text-gray-500" />
              </button>
      </div>

      {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
        {isLoading ? (
          <div className="flex items-center justify-center h-full">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : (
          messages.map((message) => (
            <div
              key={message.id}
              className={`flex ${message.userId === user?.id ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-[70%] rounded-lg p-3 ${
                  message.userId === user?.id
                    ? 'bg-blue-600 text-white'
                          : message.isAI
                          ? 'bg-purple-100 text-gray-900'
                          : 'bg-white text-gray-900 shadow-sm'
                }`}
              >
                {message.userId !== user?.id && (
                  <div className="flex items-center mb-1">
                    {message.userAvatar ? (
                      <img
                        src={message.userAvatar}
                        alt={message.userName}
                        className="w-6 h-6 rounded-full mr-2"
                      />
                    ) : (
                            <div className="w-6 h-6 rounded-full bg-gray-300 mr-2 flex items-center justify-center">
                              <span className="text-xs font-medium text-gray-600">
                                {message.userName[0]}
                              </span>
                            </div>
                    )}
                    <span className="text-sm font-medium">{message.userName}</span>
                  </div>
                )}
                      <p className="whitespace-pre-wrap break-words">{message.content}</p>
                {message.attachments && (
                  <div className="mt-2 space-y-2">
                    {message.attachments.map((attachment, index) => (
                      <div key={index} className="flex items-center">
                        {attachment.type === 'image' ? (
                          <img
                            src={attachment.url}
                            alt={attachment.name}
                            className="max-w-full rounded-lg"
                          />
                        ) : (
                          <a
                            href={attachment.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center text-sm hover:underline"
                          >
                            <Paperclip className="h-4 w-4 mr-1" />
                            {attachment.name}
                          </a>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                <div
                  className={`text-xs mt-1 ${
                    message.userId === user?.id ? 'text-blue-100' : 'text-gray-500'
                  }`}
                >
                  {new Date(message.timestamp).toLocaleTimeString()}
                </div>
              </div>
            </div>
          ))
        )}
        <div ref={messagesEndRef} />
      </div>

            {/* Input Area */}
      <div className="p-4 border-t border-gray-200">
        <div className="flex items-end space-x-2">
          <div className="flex-1">
            <textarea
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSendMessage();
                      }
                    }}
              placeholder="Type a message..."
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
              rows={1}
              style={{ minHeight: '40px', maxHeight: '120px' }}
              disabled={isUploading}
            />
          </div>
          <div className="flex items-center space-x-2">
            <button
              className="p-2 text-gray-500 hover:text-gray-700 rounded-full hover:bg-gray-100"
              title="Add emoji"
              disabled={isUploading}
            >
              <Smile className="h-5 w-5" />
            </button>
            <button
              onClick={() => fileInputRef.current?.click()}
              className="p-2 text-gray-500 hover:text-gray-700 rounded-full hover:bg-gray-100"
              title="Attach file"
              disabled={isUploading}
            >
              <Paperclip className="h-5 w-5" />
            </button>
            <button
              onClick={() => imageInputRef.current?.click()}
              className="p-2 text-gray-500 hover:text-gray-700 rounded-full hover:bg-gray-100"
              title="Add image"
              disabled={isUploading}
            >
              <Image className="h-5 w-5" />
            </button>
            <button
              onClick={handleSendMessage}
              disabled={!newMessage.trim() || isUploading}
              className="p-2 bg-blue-600 text-white rounded-full hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isUploading ? (
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
              ) : (
                <Send className="h-5 w-5" />
              )}
            </button>
          </div>
        </div>
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center bg-gray-50">
            <div className="text-center">
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Select a chat to start messaging
              </h3>
              <p className="text-gray-500">
                Choose from your existing conversations or start a new one
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Hidden file inputs */}
      <input
        type="file"
        ref={fileInputRef}
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) handleFileUpload(file);
          e.target.value = '';
        }}
        accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.zip,.rar"
      />
      <input
        type="file"
        ref={imageInputRef}
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) handleFileUpload(file);
          e.target.value = '';
        }}
        accept="image/*"
      />

      {showNewChatModal && (
        <NewChatModal
          onClose={() => setShowNewChatModal(false)}
          onCreateChat={handleCreateChat}
          workspaceUsers={workspaceUsers}
        />
      )}
    </div>
  );
} 